import { AlertOctagon, CheckCircle2, Loader2, RotateCw, X } from 'lucide-react';
import { formatDate, formatRelative } from '../../utils/formatters';
import type { PanicState } from '../../hooks/usePanicAlert';

// FE-17 — lo que el conductor ve DESPUÉS de pulsar.
//
// Es un panel fijo con cierre explícito, no un toast que se va solo: quien
// acaba de declarar una emergencia puede estar mirando la carretera durante los
// cuatro segundos que duraría un toast, y quedarse sin saber si se envió.
//
// Los cuatro estados salen de una respuesta real. El 200 idempotente no se
// disfraza de éxito nuevo: se nombra como lo que es.

interface PanicStatusPanelProps extends Pick<PanicState, 'phase' | 'intento' | 'maxIntentos' | 'alert' | 'fallo' | 'enviar' | 'descartar'> {
  plate: string;
}

export function PanicStatusPanel({ phase, intento, maxIntentos, alert, fallo, enviar, descartar, plate }: PanicStatusPanelProps) {
  if (phase === 'idle') return null;

  const vehiculo = plate || 'tu vehículo';

  return (
    <div
      className={`panic-status panic-status--${phase}`}
      role="status"
      aria-live="assertive"
    >
      {phase === 'sending' && (
        <>
          <Loader2 size={20} className="panic-status__spin" aria-hidden />
          <div className="panic-status__body">
            <strong>Enviando emergencia…</strong>
            {intento > 1 && <span>Intento {intento} de {maxIntentos}</span>}
          </div>
        </>
      )}

      {phase === 'sent' && (
        <>
          <CheckCircle2 size={20} aria-hidden />
          <div className="panic-status__body">
            <strong>Emergencia enviada</strong>
            <span>Tu supervisor la está viendo. {alert ? formatDate(alert.timestamp) : ''} · {vehiculo}</span>
          </div>
          <button type="button" className="panic-status__close" onClick={descartar} aria-label="Entendido">
            <X size={16} />
          </button>
        </>
      )}

      {phase === 'already_active' && (
        <>
          <AlertOctagon size={20} aria-hidden />
          <div className="panic-status__body">
            <strong>Ya había una emergencia activa</strong>
            <span>
              Sobre {vehiculo}, declarada {alert ? formatRelative(alert.timestamp) : 'hace un momento'}. Tu
              supervisor ya la está viendo — <strong>no se creó una nueva</strong>.
            </span>
          </div>
          <button type="button" className="panic-status__close" onClick={descartar} aria-label="Entendido">
            <X size={16} />
          </button>
        </>
      )}

      {phase === 'failed' && (
        <>
          <AlertOctagon size={20} aria-hidden />
          <div className="panic-status__body">
            <strong>NO se envió. Tu supervisor no ha sido avisado.</strong>
            <span>{fallo?.detalle}</span>
          </div>
          {/* Reintentar se ofrece siempre, también en los fallos que el hook
              no reintenta solo: si el conductor insiste es cosa suya, y
              negarle el botón en una emergencia sería peor. */}
          <button type="button" className="panic-status__retry" onClick={enviar}>
            <RotateCw size={14} /> Reintentar
          </button>
        </>
      )}
    </div>
  );
}
